// public/js/modules/map/trail-events.js
// Threshold events on the drawn asset path.
//
// The sensor sidebar offers a per-metric "Show on map" switch plus from/to
// threshold fields. Every history point whose value matches the threshold gets
// a small diamond pin ON the path (colour = the metric's chart colour); a click
// shows the event details. Threshold semantics:
//   from only        → value >= from        (e.g. shock >= 5 g)
//   to only          → value <= to          (e.g. temperature <= 0 °C)
//   from + to        → from <= value <= to
// Thresholds persist per metric group in localStorage (global, cross-asset);
// the switches themselves always start OFF.

import { mapInstance, currentOpenPopup, setCurrentOpenPopup } from './state.js';
import { nearestRoutePosition } from './history.js';
import { formatLocalTime } from '../../utils/asset-utils.js';
import { convertNumber, displayUnit } from '../../utils/units.js';

const LS_KEY = 'map.eventThresholds';
const MAX_PINS_PER_GROUP = 300;

// groupId → { enabled, meta, supplier, markers: [] }
const active = new Map();

function loadThresholds() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; } catch (e) { return {}; }
}
function saveThresholds(all) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(all)); } catch (e) {}
}

export function getEventThresholds(groupId) {
  const t = loadThresholds()[groupId] || {};
  return { from: t.from ?? null, to: t.to ?? null };
}

export function setEventThresholds(groupId, { from, to }) {
  const all = loadThresholds();
  all[groupId] = {
    from: Number.isFinite(from) ? from : null,
    to: Number.isFinite(to) ? to : null,
  };
  saveThresholds(all);
  const entry = active.get(groupId);
  if (entry && entry.enabled) computeAndDraw(groupId);
}

export function isEventGroupEnabled(groupId) {
  return !!active.get(groupId)?.enabled;
}

/**
 * Toggle a metric group's event pins.
 * @param {string} groupId   stable id (slug of the group title)
 * @param {boolean} on
 * @param {object} meta      { title, unit, series: [{key?, get?, label, color}] }
 * @param {function} supplier () => history points (raw rows, any order)
 */
export function setEventGroupEnabled(groupId, on, meta = null, supplier = null) {
  let entry = active.get(groupId);
  if (!entry) {
    entry = { enabled: false, meta: null, supplier: null, markers: [] };
    active.set(groupId, entry);
  }
  if (meta) entry.meta = meta;
  if (supplier) entry.supplier = supplier;
  entry.enabled = !!on;
  if (entry.enabled) computeAndDraw(groupId);
  else removePins(entry);
}

// Recompute every enabled group (after new history points were loaded).
export function refreshTrailEvents() {
  active.forEach((entry, groupId) => { if (entry.enabled) computeAndDraw(groupId); });
}

// Drop all pins; called when the route is cleared (asset switch / panel close).
// Switch states reset too — the next asset starts with a clean map.
export function clearTrailEvents() {
  active.forEach(entry => { removePins(entry); entry.enabled = false; });
}

function removePins(entry) {
  (entry.markers || []).forEach(m => { try { m.remove(); } catch (e) {} });
  entry.markers = [];
}

function matches(v, { from, to }) {
  if (from == null && to == null) return false;   // no threshold set → no events
  if (from != null && v < from) return false;
  if (to != null && v > to) return false;
  return true;
}

function computeAndDraw(groupId) {
  const entry = active.get(groupId);
  const map = mapInstance;
  if (!entry || !entry.meta || !entry.supplier || !map) return;
  removePins(entry);

  const thresholds = getEventThresholds(groupId);
  if (thresholds.from == null && thresholds.to == null) return;

  const points = entry.supplier() || [];
  const events = [];
  for (const p of points) {
    const tMs = p._t ?? new Date(p.timestamp).getTime();
    if (!Number.isFinite(tMs)) continue;
    for (const s of entry.meta.series) {
      const raw = s.get ? s.get(p) : p[s.key];
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      // Compare in the user's DISPLAY unit — the fields are labeled with it.
      const v = convertNumber(n, entry.meta.unit);
      if (!matches(v, thresholds)) continue;
      events.push({ point: p, series: s, value: v, tMs });
      break;   // one pin per row is enough even if several series match
    }
  }

  if (!events.length) return;

  // Newest first; cap so a broad threshold cannot flood the map.
  events.sort((a, b) => b.tMs - a.tMs);
  const capped = events.slice(0, MAX_PINS_PER_GROUP);
  if (events.length > capped.length) {
    window.showToast?.(`${entry.meta.title}: showing the ${MAX_PINS_PER_GROUP} most recent of ${events.length} events`, 'info');
  }

  const markers = [];
  for (const ev of capped) {
    // Use the row's own fix; sensor-only heartbeats snap to the nearest path
    // point in time.
    let lat = parseFloat(ev.point.lat);
    let lon = parseFloat(ev.point.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      const near = nearestRoutePosition(ev.tMs);
      if (!near) continue;
      lat = near.lat; lon = near.lon;
    }
    markers.push(makeEventMarker(map, entry, ev, [lon, lat]));
  }
  entry.markers = markers;
}

function makeEventMarker(map, entry, ev, lngLat) {
  const color = ev.series.color || '#dc2626';
  // Outer element stays untransformed (MapLibre positions it via transform);
  // the inner square is the rotated diamond.
  const el = document.createElement('div');
  el.className = 'map-event-marker';
  el.innerHTML = `<div class="map-event-marker-shape" style="background:${color};"></div>`;
  el.title = `${entry.meta.title}: ${round2(ev.value)} ${displayUnit(entry.meta.unit)}`.trim();

  const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map);

  el.addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentOpenPopup) { try { currentOpenPopup.remove(); } catch (err) {} }
    const unit = displayUnit(entry.meta.unit);
    const dev = ev.point.deviceId ? `<br><span class="text-muted" style="font-size:0.8rem;">${escapeHtml(String(ev.point.deviceId))}</span>` : '';
    const html = `
      <div style="font-size:0.9rem; min-width:200px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
          <span style="width:10px;height:10px;background:${ev.series.color};border:1px solid #fff;transform:rotate(45deg);display:inline-block;"></span>
          <strong>${escapeHtml(entry.meta.title)}</strong>
        </div>
        ${escapeHtml(ev.series.label)}: <strong>${round2(ev.value)}${unit ? ' ' + unit : ''}</strong><br>
        ${formatLocalTime(ev.point.timestamp)}<br>
        <span class="text-muted" style="font-size:0.8rem;">${lngLat[1].toFixed(5)}, ${lngLat[0].toFixed(5)}</span>
        ${dev}
      </div>`;
    const popup = new maplibregl.Popup({ closeOnClick: true, offset: 12 })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(map);
    setCurrentOpenPopup(popup);
  });

  return marker;
}

function round2(n) { return Math.round(n * 100) / 100; }

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// New/extended path data → recompute; cleared path → drop everything.
window.addEventListener('assetHistoryLoaded', () => refreshTrailEvents());
window.addEventListener('routeCleared', () => clearTrailEvents());
